import "@config/i18n";
import { Suspense, useEffect, useState } from "react";
import type { MantineThemeOverride } from "@mantine/core";
// MUST come before any layered stylesheet. The first `@layer` statement the
// document sees fixes the order for everything after it, so importing Mantine's
// layered build first would establish `mantine` ahead of `beehive` and let a
// copy of Mantine loaded by another module override BeeHive's host styles.
// Design-system 0.3.0 ships this file precisely to replace the five
// hand-maintained src/styles/layers.css copies the frontends grew separately.
import "@xplatform/design-system/styles.layer-order.css";
import "@mantine/core/styles.layer.css";
import "@xplatform/design-system/styles.layer.css";
import { LoadingScreen, XPlatformProvider } from "@xplatform/design-system";
import { Routes, Route } from "react-router-dom";
import { injectStylesheet } from "@utils/injectStyles";
import { ModuleContextProvider } from "@src/ContextProvider";
import { ReleasesPage } from "@pages/ReleasesPage";
import { ManageReleasesPage } from "@pages/ManageReleasesPage";

const PORTAL_ID = "releases-portal-root";

const ModuleApp = () => {
  const [shellOverride, setShellOverride] = useState<
    MantineThemeOverride | undefined
  >(undefined);

  useEffect(() => {
    import("xplatform_shell/theme")
      .then((mod) => {
        if (mod.theme) setShellOverride(mod.theme);
      })
      .catch(() => {});
    const base = import.meta.env.BASE_URL || "/";
    injectStylesheet(`${base}assets/style.css`);
  }, []);

  return (
    <XPlatformProvider theme={shellOverride}>
      <ModuleContextProvider>
        <Suspense fallback={<LoadingScreen message="Loading..." />}>
          <Routes>
            <Route index element={<ReleasesPage />} />
            {/* Authoring lives here rather than in the admin module, so every
                release-note surface stays in one repo without inventing a
                remote-consumes-remote dependency. Gated inside the page. */}
            <Route path="manage" element={<ManageReleasesPage />} />
          </Routes>
        </Suspense>
      </ModuleContextProvider>
      <div
        id={PORTAL_ID}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
          zIndex: 9999,
        }}
      />
    </XPlatformProvider>
  );
};

export default ModuleApp;
